import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Paperclip, RefreshCw, MessageSquare, CornerDownRight } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  mattermostService,
  type MMChannel,
  type MMMessage,
  type MMStatus,
  type MMThread,
} from '../../services/mattermostService';
import { Modal } from '../../components/Modal';

function fmt(ts?: string) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('fa-IR');
  } catch {
    return ts;
  }
}

export function MattermostMessagesPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<MMStatus | null>(null);
  const [channels, setChannels] = useState<MMChannel[]>([]);
  const [channelId, setChannelId] = useState(params.get('channelId') || '');
  const [messages, setMessages] = useState<MMMessage[]>([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState('');
  const [detail, setDetail] = useState<MMMessage | null>(null);
  const [thread, setThread] = useState<MMThread | null>(null);
  const perPage = 40;

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const [st, ch] = await Promise.all([mattermostService.status(), mattermostService.channels()]);
      setStatus(st);
      setChannels(ch);
      if (!channelId && ch[0]?.id) setChannelId(ch[0].id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در اتصال Mattermost');
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  const loadMessages = useCallback(async () => {
    if (!channelId) {
      setMessages([]);
      return;
    }
    setMsgLoading(true);
    try {
      const res = await mattermostService.messages({ channelId, page, perPage });
      setMessages(res.data ?? []);
      setHasNext(!!res.hasNext);
      setFetchedAt(res.fetchedAt || '');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت پیام‌ها');
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  }, [channelId, page]);

  useEffect(() => {
    void loadMeta();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  const openDetail = async (m: MMMessage) => {
    setDetail(m);
    setThread(null);
    try {
      const [full, th] = await Promise.all([
        mattermostService.message(m.id),
        mattermostService.thread(m.id),
      ]);
      setDetail(full);
      setThread(th);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">پیام‌های Mattermost</h1>
          <p className="mt-1 text-sm text-slate-500">
            فقط خواندنی · از طریق حساب یکپارچه‌سازی
            {status?.username ? ` (@${status.username})` : ''}
            {fetchedAt ? ` · آخرین دریافت: ${fmt(fetchedAt)}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void loadMeta();
            void loadMessages();
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw className={`h-4 w-4 ${msgLoading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      {status && !status.configured ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Mattermost پیکربندی نشده. متغیرهای{' '}
          <code className="text-xs">MATTERMOST_BASE_URL</code> و{' '}
          <code className="text-xs">MATTERMOST_ACCESS_TOKEN</code> را در API تنظیم کنید.
        </div>
      ) : null}
      {status?.configured && !status.ok ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          اتصال ناموفق: {status.error || 'unknown'}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <aside className="rounded-xl bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between px-1">
            <h2 className="text-sm font-bold text-slate-800">کانال‌ها</h2>
            <Link to="/mattermost/channels" className="text-xs text-teal-700 hover:underline">
              همه
            </Link>
          </div>
          {loading ? (
            <p className="p-3 text-sm text-slate-500">در حال بارگذاری…</p>
          ) : channels.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">کانالی در دسترس نیست.</p>
          ) : (
            <ul className="max-h-[70vh] space-y-1 overflow-y-auto">
              {channels.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setPage(0);
                      setChannelId(c.id);
                    }}
                    className={`w-full rounded-lg px-3 py-2 text-right text-sm ${
                      channelId === c.id ? 'bg-slate-900 text-white' : 'hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <div className="font-medium">{c.displayName || c.name}</div>
                    <div className={`text-xs ${channelId === c.id ? 'text-slate-300' : 'text-slate-500'}`}>
                      {c.type}
                      {c.teamName ? ` · ${c.teamName}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="rounded-xl bg-white shadow-sm">
          {msgLoading && messages.length === 0 ? (
            <p className="p-8 text-center text-slate-500">در حال بارگذاری پیام‌ها…</p>
          ) : messages.length === 0 ? (
            <p className="p-8 text-center text-slate-500">پیامی نیست.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {messages.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => void openDetail(m)}
                    className="flex w-full flex-col gap-1 px-4 py-3 text-right hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="font-semibold text-slate-800">{m.displayName || m.username}</span>
                      <span>@{m.username}</span>
                      <span>{fmt(m.createAt)}</span>
                      {m.edited ? <span className="rounded bg-slate-100 px-1.5">ویرایش‌شده</span> : null}
                      {m.isReply ? (
                        <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5">
                          <CornerDownRight className="h-3 w-3" /> پاسخ
                        </span>
                      ) : null}
                      {m.hasAttachments ? (
                        <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5">
                          <Paperclip className="h-3 w-3" /> پیوست
                        </span>
                      ) : null}
                      {m.replyCount > 0 ? (
                        <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5">
                          <MessageSquare className="h-3 w-3" /> {m.replyCount}
                        </span>
                      ) : null}
                      {m.reactionsCount > 0 ? <span className="rounded bg-slate-100 px-1.5">☺ {m.reactionsCount}</span> : null}
                    </div>
                    <p className="line-clamp-3 whitespace-pre-wrap text-sm text-slate-800">{m.message || '—'}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <button
              type="button"
              disabled={page <= 0 || msgLoading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              جدیدتر
            </button>
            <span className="text-xs text-slate-500">صفحه {page + 1}</span>
            <button
              type="button"
              disabled={!hasNext || msgLoading}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              قدیمی‌تر
            </button>
          </div>
        </section>
      </div>

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title="جزئیات پیام" size="lg">
        {detail ? (
          <div className="space-y-3 text-sm" dir="rtl">
            <div className="text-slate-500">
              {detail.displayName} (@{detail.username}) · {fmt(detail.createAt)}
              {detail.channelDisplayName ? ` · #${detail.channelDisplayName}` : ''}
            </div>
            <pre className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-slate-900">{detail.message}</pre>
            {detail.attachments?.length ? (
              <div>
                <h3 className="mb-2 font-semibold">پیوست‌ها</h3>
                <ul className="space-y-1">
                  {detail.attachments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-3 py-2">
                      <span>
                        {a.name || a.id}{' '}
                        <span className="text-xs text-slate-500">
                          {a.mimeType} · {(a.size / 1024).toFixed(1)} KB
                        </span>
                      </span>
                      {a.downloadAvailable || a.id ? (
                        <a
                          className="text-xs font-semibold text-teal-700 hover:underline"
                          href={mattermostService.fileDownloadUrl(a.id)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            const token = localStorage.getItem('adminToken');
                            void fetch(mattermostService.fileDownloadUrl(a.id), {
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                            })
                              .then(async (r) => {
                                if (!r.ok) throw new Error('دانلود ناموفق');
                                const blob = await r.blob();
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = a.name || 'file';
                                link.click();
                                URL.revokeObjectURL(url);
                              })
                              .catch((err) => toast.error(err instanceof Error ? err.message : 'خطا'));
                          }}
                        >
                          دانلود
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {thread?.root && thread.root.id !== detail.id ? (
              <div>
                <h3 className="mb-1 font-semibold">پیام ریشه</h3>
                <p className="rounded bg-slate-50 p-2 text-slate-700">{thread.root.message}</p>
              </div>
            ) : null}
            {thread?.replies?.length ? (
              <div>
                <h3 className="mb-2 font-semibold">پاسخ‌های رشته ({thread.replies.length})</h3>
                <ul className="max-h-60 space-y-2 overflow-y-auto">
                  {thread.replies.map((r) => (
                    <li key={r.id} className="rounded border border-slate-100 p-2">
                      <div className="text-xs text-slate-500">
                        {r.displayName} · {fmt(r.createAt)}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{r.message}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {detail.permalink ? (
              <a href={detail.permalink} target="_blank" rel="noreferrer" className="text-teal-700 hover:underline">
                باز کردن در Mattermost
              </a>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
